import {
  useEmitErrorNotification,
  useEmitSuccessNotification,
} from "$routes/_lib/use-emit-notification";
import {
  GetFacilityQueuesDocument,
  type GetFacilityQueuesQuery,
} from "$routes/patient-queue-management/_lib/__generated__/queries.gql";
import { QUEUE_PATIENT_FRAGMENT } from "$routes/patient-queue-management/_lib/queries";
import { gql, useApolloClient } from "@apollo/client";
import { useCallback } from "react";
import { useIntl } from "react-intl";
import {
  type AddQueuePatientMutation,
  type MoveQueuePatientMutation,
  QueryForPatientDocument,
  type QueryForPatientQuery,
  useAddQueuePatientMutation,
  useMoveQueuePatientMutation,
  useRemoveQueuePatientMutation,
} from "./__generated__/graphql.gql";

const PATIENT_QUEUE_FRAGMENT = gql`
  fragment PatientQueueFragment on PatientQueue {
    _id
    name
  }
`;

const PATIENT_QUEUE_WITH_PATIENTS_FRAGMENT = gql`
  ${QUEUE_PATIENT_FRAGMENT}

  fragment PatientQueueWithPatientsFragment on PatientQueue {
    _id
    name
    patients {
      ...QueuePatientFragment
    }
  }
`;

gql`
  query QueryForPatient($patientUuid: ID!) {
    queueForPatient(input: { patientUuid: $patientUuid }) {
      _id
    }
  }
`;

gql`
  ${PATIENT_QUEUE_FRAGMENT}

  query PatientQueueDropdown($patientUuid: ID!) {
    facilityQueues {
      ...PatientQueueFragment
    }

    queueForPatient(input: { patientUuid: $patientUuid }) {
      _id
    }
  }
`;

gql`
  ${PATIENT_QUEUE_WITH_PATIENTS_FRAGMENT}

  mutation AddQueuePatient($input: AddQueuePatientInput!) {
    addQueuePatient(input: $input) {
      ...PatientQueueWithPatientsFragment
    }
  }
`;

gql`
  ${PATIENT_QUEUE_WITH_PATIENTS_FRAGMENT}

  mutation RemoveQueuePatient($input: RemoveQueuePatientInput!) {
    removeQueuePatient(input: $input) {
      ...PatientQueueWithPatientsFragment
    }
  }
`;

gql`
  ${PATIENT_QUEUE_WITH_PATIENTS_FRAGMENT}

  mutation MoveQueuePatient($input: MoveQueuePatientInput!) {
    moveQueuePatient(input: $input) {
      ...PatientQueueWithPatientsFragment
    }
  }
`;

function useUpdateCurrentQueue(patientUuid: string) {
  const apolloClient = useApolloClient();
  return (
    newQueue:
      | AddQueuePatientMutation["addQueuePatient"]
      | MoveQueuePatientMutation["moveQueuePatient"]
      | null,
  ) => {
    const currentQueueResult =
      apolloClient.cache.readQuery<QueryForPatientQuery>({
        query: QueryForPatientDocument,
        variables: { patientUuid },
      });

    apolloClient.cache.writeQuery<QueryForPatientQuery>({
      query: QueryForPatientDocument,
      variables: { patientUuid },
      data: { queueForPatient: newQueue },
    });

    const existingQueues = apolloClient.cache.readQuery<GetFacilityQueuesQuery>(
      {
        query: GetFacilityQueuesDocument,
      },
    );
    if (!existingQueues) {
      // If the queue management page has not been visited yet, there is no cache to update
      return;
    }

    const oldQueue = currentQueueResult?.queueForPatient;

    // Remove patient from old queue if they were in one
    if (!oldQueue) return;

    const nextQueues = existingQueues.facilityQueues.map((queue) => {
      if (oldQueue._id === queue._id) {
        return {
          ...queue,
          patients: queue.patients.filter(
            (patient) => patient.uuid !== patientUuid,
          ),
        };
      }

      return queue;
    });

    apolloClient.cache.writeQuery<GetFacilityQueuesQuery>({
      query: GetFacilityQueuesDocument,
      data: { facilityQueues: nextQueues },
    });
  };
}

export function useAddPatientToQueue(
  patientUuid: string,
  currentQueueId: string,
) {
  const emitSuccessNotification = useEmitSuccessNotification();
  const emitErrorNotification = useEmitErrorNotification();
  const { formatMessage } = useIntl();

  const updateCurrentQueue = useUpdateCurrentQueue(patientUuid);
  const [addQueuePatient, addQueuePatientResult] = useAddQueuePatientMutation({
    onCompleted(data) {
      updateCurrentQueue(data.addQueuePatient);
      emitSuccessNotification(
        formatMessage({
          id: "patientQueueing.dropdown.mutation.add.success",
          defaultMessage: "Added patient to queue",
        }),
      );
    },
    onError() {
      emitErrorNotification(
        formatMessage({
          id: "patientQueueing.dropdown.mutation.add.error",
          defaultMessage: "Failed to add patient to queue",
        }),
      );
    },
  });

  const [moveQueuePatient, moveQueuePatientResult] =
    useMoveQueuePatientMutation({
      onCompleted(data) {
        updateCurrentQueue(data.moveQueuePatient);
        emitSuccessNotification(
          formatMessage({
            id: "patientQueueing.dropdown.mutation.move.success",
            defaultMessage: "Moved patient to new queue",
          }),
        );
      },
      onError() {
        emitErrorNotification(
          formatMessage({
            id: "patientQueueing.dropdown.mutation.move.error",
            defaultMessage: "Failed to move patient to new queue",
          }),
        );
      },
    });

  const isMove = !!currentQueueId;

  const handler = useCallback(
    async (nextQueueId: string) => {
      if (isMove) {
        await moveQueuePatient({
          variables: {
            input: { patientUuid, newQueueId: nextQueueId },
          },
        });
      } else {
        await addQueuePatient({
          variables: {
            input: { patientUuid, queueId: nextQueueId },
          },
        });
      }
    },
    [addQueuePatient, moveQueuePatient, patientUuid, isMove],
  );

  const result = isMove ? moveQueuePatientResult : addQueuePatientResult;

  // Return both results explicitly in case we are interested in the
  // result of the add mutation after the fact. This is needed
  // as the result of the add mutation causes a `currentQueueId` to then
  // exist causing result to become `moveQueuePatientResult`.
  return [
    handler,
    result,
    addQueuePatientResult,
    moveQueuePatientResult,
  ] as const;
}

export function useRemovePatientFromQueue(patientUuid: string) {
  const emitSuccessNotification = useEmitSuccessNotification();
  const emitErrorNotification = useEmitErrorNotification();
  const { formatMessage } = useIntl();

  const updateCurrentQueue = useUpdateCurrentQueue(patientUuid);
  const [removeQueuePatient, removeQueuePatientResult] =
    useRemoveQueuePatientMutation({
      onCompleted() {
        updateCurrentQueue(null);
        emitSuccessNotification(
          formatMessage({
            id: "patientQueueing.dropdown.mutation.remove.success",
            defaultMessage: "Removed patient from queue",
          }),
        );
      },
      onError() {
        emitErrorNotification(
          formatMessage({
            id: "patientQueueing.dropdown.mutation.remove.error",
            defaultMessage: "Failed to remove patient from queue",
          }),
        );
      },
    });

  const handler = useCallback(async () => {
    await removeQueuePatient({
      variables: {
        input: { patientUuid },
      },
    });
  }, [removeQueuePatient, patientUuid]);

  return [handler, removeQueuePatientResult] as const;
}
